import { observeWhenVisible } from '../../../core/utils/dom.js';
import { getCurrentLanguage } from '../../translations.js';
import { getAuthToken } from '../videoScheduleApi.js';
import { fetchStandaloneImageObjectUrl } from '../standaloneHdUpgrade.js';
import { fetchStandaloneMediaPage, PAGE_LIMIT } from '../standaloneMediaApi.js';
import loginModalController from '../../components/LoginModalController.js';

const MODAL_ID = 'create-video-image-picker-modal';

const getT = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['create-video'] ?? window.translations?.en?.['create-video'];
  return t?.[key] ?? fallback;
};

const isHdImage = (item) => Boolean(item?.is_hd);

const ensureModal = () => {
  let modal = document.getElementById(MODAL_ID);
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${MODAL_ID}-title`);
  const panel = document.createElement('div');
  panel.className = 'picker-panel';
  modal.appendChild(panel);
  document.body.appendChild(modal);
  return modal;
};

const removeHdTooltip = () => {
  document.querySelectorAll('.picker-hd-required-tooltip').forEach((el) => el.remove());
};

const positionTooltip = (tooltip, anchor) => {
  const rect = anchor.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const width = tooltipRect.width || 220;
  const spaceOnLeft = rect.left;
  const spaceOnRight = window.innerWidth - rect.right;
  tooltip.style.transform = '';
  if (spaceOnRight >= width + 10) {
    tooltip.style.left = `${rect.right + 10}px`;
    tooltip.style.right = '';
    tooltip.style.top = `${rect.top}px`;
  } else if (spaceOnLeft >= width + 10) {
    tooltip.style.right = `${window.innerWidth - rect.left + 10}px`;
    tooltip.style.left = '';
    tooltip.style.top = `${rect.top}px`;
  } else {
    tooltip.style.left = '50%';
    tooltip.style.right = '';
    tooltip.style.top = `${rect.bottom + 10}px`;
    tooltip.style.transform = 'translateX(-50%)';
  }
};

const showHdTooltip = (anchor) => {
  removeHdTooltip();
  const tooltip = document.createElement('div');
  tooltip.className = 'description-tooltip picker-hd-required-tooltip is-visible';
  tooltip.setAttribute('role', 'tooltip');
  const text = document.createElement('p');
  text.className = 'description-tooltip-text';
  text.textContent = getT(
    'free_video_hd_required_tooltip',
    'Upgrade this image to HD before turning it into a video.',
  );
  tooltip.appendChild(text);
  tooltip.addEventListener('click', (event) => event.stopPropagation());
  document.body.appendChild(tooltip);
  positionTooltip(tooltip, anchor);
  const closeTooltip = (event) => {
    if (event?.type === 'click' && event.target instanceof Element) {
      if (tooltip.contains(event.target) || anchor.contains(event.target)) return;
    }
    tooltip.remove();
    document.removeEventListener('click', closeTooltip);
    document.removeEventListener('keydown', handleEscape);
  };
  const handleEscape = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeTooltip(event);
    }
  };
  setTimeout(() => {
    document.addEventListener('click', closeTooltip);
    document.addEventListener('keydown', handleEscape);
  }, 100);
};

const renderStatus = (grid, kind) => {
  grid.replaceChildren();
  const status = document.createElement('div');
  status.className = 'picker-status';
  const message = document.createElement('p');
  message.className = 'picker-status__message';
  if (kind === 'loading') {
    message.textContent = getT('loading', 'Loading…');
    status.appendChild(message);
  } else if (kind === 'login') {
    message.textContent = getT('free_video_login', 'Log in to pick an image.');
    const loginButton = document.createElement('button');
    loginButton.type = 'button';
    loginButton.className = 'my-gallery-login-btn';
    loginButton.textContent = getT('free_video_login_button', 'Log in');
    loginButton.tabIndex = 0;
    loginButton.addEventListener('click', () => {
      loginModalController?.show?.();
    });
    status.appendChild(message);
    status.appendChild(loginButton);
  } else if (kind === 'error') {
    message.textContent = getT('error', 'Could not load images.');
    status.appendChild(message);
  } else {
    message.textContent = getT('free_video_gallery_empty', 'No images yet.');
    status.appendChild(message);
  }
  grid.appendChild(status);
};

export const openFreeVideoImagePicker = async ({ onSelect, selectedKey = '', onClose } = {}) => {
  const modal = ensureModal();
  const panel = modal.querySelector('.picker-panel');
  if (!panel) return;

  let nextCursor = null;
  let isLoading = false;
  let isLoadingMore = false;
  let loadGeneration = 0;
  let closed = false;
  let showLowres = false;
  let sentinelObserver = { disconnect: () => {} };

  const handleClose = () => {
    if (closed) return;
    closed = true;
    sentinelObserver.disconnect();
    if (cleanup.keydown) document.removeEventListener('keydown', cleanup.keydown, true);
    cleanup.keydown = null;
    removeHdTooltip();
    modal.classList.remove('show');
    modal.onclick = null;
    onClose?.();
  };

  const cleanup = { keydown: null };

  const appendTiles = (grid, items, token) => {
    items.forEach((item) => {
      const hd = isHdImage(item);
      const card = document.createElement('div');
      card.className = 'image-picker-card';
      card.setAttribute('role', 'button');
      card.tabIndex = 0;
      card.setAttribute('aria-disabled', hd ? 'false' : 'true');
      card.setAttribute(
        'aria-label',
        hd
          ? getT('free_video_pick_image', 'Use this image')
          : getT('free_video_hd_required_tooltip', 'Upgrade this image to HD before turning it into a video.'),
      );
      if (!hd) card.classList.add('image-picker-card--lowres');
      if (selectedKey && String(item.output_key) === String(selectedKey)) {
        card.classList.add('is-selected');
      }
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      card.appendChild(img);
      void (async () => {
        const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key });
        if (objectUrl && img.isConnected) img.src = objectUrl;
      })();

      const handleActivate = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!hd) {
          showHdTooltip(card);
          return;
        }
        handleClose();
        onSelect?.(item);
      };

      if (hd) {
        card.addEventListener('click', handleActivate);
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          handleActivate(event);
        });
      } else {
        card.addEventListener('mouseenter', () => showHdTooltip(card));
        card.addEventListener('focus', () => showHdTooltip(card));
        card.addEventListener('click', handleActivate);
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          handleActivate(event);
        });
      }
      grid.appendChild(card);
    });
  };

  const loadPage = async (grid, sentinel, { append = false } = {}) => {
    if (append) {
      if (!nextCursor || isLoadingMore) return;
    }
    const generation = append ? loadGeneration : ++loadGeneration;
    if (append) isLoadingMore = true;
    else isLoading = true;
    if (!append) {
      nextCursor = null;
      renderStatus(grid, 'loading');
    }
    try {
      const token = await getAuthToken();
      if (!token) {
        if (generation !== loadGeneration) return;
        renderStatus(grid, 'login');
        return;
      }
      const result = await fetchStandaloneMediaPage({
        token,
        cursor: append ? nextCursor : null,
        filter: 'images',
        hd: showLowres ? 'both' : 'hires',
        limit: PAGE_LIMIT,
      });
      if (generation !== loadGeneration) return;
      const items = Array.isArray(result?.items) ? result.items : [];
      if (!append) {
        grid.replaceChildren();
        if (items.length === 0) {
          renderStatus(grid, 'empty');
          nextCursor = null;
          sentinel.hidden = true;
          return;
        }
      }
      if (items.length > 0) appendTiles(grid, items, token);
      nextCursor = result?.nextCursor || null;
      sentinel.hidden = !nextCursor;
    } catch (error) {
      if (generation !== loadGeneration) return;
      if (!append) renderStatus(grid, error?.code === 'login' ? 'login' : 'error');
      console.error('[freeVideoImagePicker] load failed', error);
    } finally {
      if (generation === loadGeneration) {
        if (append) isLoadingMore = false;
        else isLoading = false;
      }
    }
  };

  panel.replaceChildren();
  const header = document.createElement('div');
  header.className = 'picker-header';
  const title = document.createElement('div');
  title.className = 'picker-title';
  title.id = `${MODAL_ID}-title`;
  title.textContent = getT('free_video_picker_title', 'Pick an image');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'picker-close';
  closeBtn.textContent = '×';
  closeBtn.setAttribute('aria-label', getT('free_video_crop_cancel', 'Cancel'));
  closeBtn.addEventListener('click', handleClose);
  header.append(title, closeBtn);

  const toggleRow = document.createElement('div');
  toggleRow.className = 'picker-hd-toggle-row';
  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.className = 'picker-hd-toggle';
  toggleBtn.tabIndex = 0;
  const syncToggleLabel = () => {
    const label = showLowres
      ? getT('free_video_hide_lowres', 'Hide low-res images')
      : getT('free_video_show_lowres', 'Show low-res images');
    toggleBtn.textContent = label;
    toggleBtn.setAttribute('aria-label', label);
    toggleBtn.setAttribute('aria-pressed', showLowres ? 'true' : 'false');
  };
  const handleToggleLowres = () => {
    showLowres = !showLowres;
    syncToggleLabel();
    removeHdTooltip();
    void loadPage(grid, sentinel, { append: false });
  };
  toggleBtn.addEventListener('click', handleToggleLowres);
  syncToggleLabel();
  toggleRow.append(toggleBtn);

  const listWrap = document.createElement('div');
  listWrap.className = 'picker-list';
  const grid = document.createElement('div');
  grid.className = 'picker-grid image-picker-grid';
  const sentinel = document.createElement('div');
  sentinel.className = 'infinite-scroll-trigger';
  sentinel.hidden = true;
  listWrap.append(grid, sentinel);
  panel.append(header, toggleRow, listWrap);

  sentinelObserver = observeWhenVisible(sentinel, () => {
    if (nextCursor) void loadPage(grid, sentinel, { append: true });
  }, { root: listWrap, rootMargin: '100px' });

  cleanup.keydown = (event) => {
    const key = event.key || event.code;
    if (key !== 'Escape' && key !== 'Esc') return;
    if (document.querySelector('.picker-hd-required-tooltip')) {
      event.preventDefault();
      event.stopPropagation();
      removeHdTooltip();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    handleClose();
  };
  document.addEventListener('keydown', cleanup.keydown, true);
  modal.onclick = (event) => {
    if (event.target === modal) handleClose();
  };

  modal.classList.add('show');
  await loadPage(grid, sentinel, { append: false });
};

export const attachFreeVideoImagePickerToTrigger = ({ triggerEl, onSelect, getSelectedKey } = {}) => {
  if (!triggerEl) return;
  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    if (triggerEl.dataset.pickerOpening === 'true') return;
    triggerEl.dataset.pickerOpening = 'true';
    triggerEl.setAttribute('aria-expanded', 'true');
    void openFreeVideoImagePicker({
      onSelect,
      selectedKey: typeof getSelectedKey === 'function' ? getSelectedKey() : '',
      onClose: () => {
        triggerEl.dataset.pickerOpening = 'false';
        triggerEl.setAttribute('aria-expanded', 'false');
      },
    });
  };
  triggerEl.addEventListener('click', handleOpen);
  triggerEl.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') handleOpen(event);
  });
};
